<template>
    <section>
        <b-modal :id="modalId" :ref="modalId" size="xl" title="Pick Ticket" no-close-on-backdrop>
            <b-row>
                <b-col cols="2">
                    <dt>Sales Order No</dt>
                    <dd>{{ scheduleObj.salesOrderId }}</dd>
                </b-col>
                <b-col cols="2">
                    <dt>Sales Order Date</dt>
                    <dd>{{ date(scheduleObj.soDate) }}</dd>
                </b-col>
                <b-col cols="2">
                    <dt>Customer</dt>
                    <dd>{{ scheduleObj.companyName }}</dd>
                </b-col>
                <b-col cols="2">
                    <dt>Contact</dt>
                    <dd>{{ scheduleObj.contactFirstName + ' ' + scheduleObj.contactLastName }}</dd>
                </b-col>
                <b-col cols="2">
                    <dt>Sales Rep</dt>
                    <dd>{{ scheduleObj.salesPersonUserFullName }}</dd>
                </b-col>
            </b-row>
            <b-row class="mt-2">
                <b-col cols="1">
                    <b-form-group label="Scheduled Qty">
                        <b-form-input type="number" v-model="scheduleObj.qtyToShip" :class="{'is-invalid-qty-to-ship': isInvalidQtyToShip}" />
                    </b-form-group>
                </b-col>
                <b-col cols="1">
                    <b-form-group label="Max Qty">
                        <b-form-input v-model="scheduleObj.quantity" disabled />
                    </b-form-group>
                </b-col>
                <b-col cols="1">
                    <b-form-group label="Date Due">
                        <b-form-input :value="date(scheduleObj.customerDueDate)" disabled />
                    </b-form-group>
                </b-col>
                <b-col cols="1">
                    <b-form-group label="Ship via">
                        <b-form-input v-model="scheduleObj.shippingProviderName" disabled />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row class="mt-2">
                <b-col>
                    <h6>Below are the inventory items that have been allocated for the sales order. Ensure to <strong>save</strong> your changes.</h6>
                    <Grid
                        :pageable="false"
                        :resizable="true"
                        :reorderable="true"
                        :sortable="false"
                        :row-height="25"
                        :data-items="itemData"
                        :columns="prepareColumnsData({ initialColumns: fields, settingsColumns: gridSettings.columns })"
                        class="grid mt-1">
                        <template v-slot:commands="{props}">
                            <td>
                                <div class="d-flex justify-content-center align-items-center">
                                    <b-form-checkbox v-model="props.dataItem.selected" />
                                </div>
                            </td>
                        </template>
                        <template v-slot:formatSeparator="{props}">
                            <td>{{ commafy(props.dataItem[props.field]) }}</td>
                        </template>
                        <template v-slot:qtyToPull="{props}">
                            <td>
                                <div class="d-flex justify-content-center align-items-center">
                                    <b-form-input type="number" v-model="props.dataItem.quantityToPull" @input="onPull(props.dataItem.quantityToPull, props.dataItem.id)" :disabled="props.dataItem.isConfirmed" />
                                </div>
                            </td>
                        </template>
                        <template v-slot:confirm="{props}">
                            <td>
                                <div class="d-flex justify-content-center align-items-center">
                                    <b-button size="sm" :variant="props.dataItem.isConfirmed ? 'success' : 'info'" @click="onConfirmPickItem(props.dataItem)">
                                        <span v-if="props.dataItem.isConfirmed">
                                            <i class="fa fa-check-circle"></i> Confirmed
                                        </span>
                                        <span v-else>
                                            <i class="fa fa-info-circle"></i> Confirm
                                        </span>

                                    </b-button>
                                </div>
                            </td>
                        </template>
                    </Grid>
                </b-col>
            </b-row>
            <template slot="modal-footer">
                <b-button variant="primary" class="mr-auto" @click="$refs['pick-ticket-reports-modal'].showModal()"> Print</b-button>
                <b-button v-show="showAddToInvoice" variant="info" @click="onAddToInvoice">Add To Invoice</b-button>
                <b-button @click="closeModal">Cancel</b-button>
                <b-button variant="primary" @click="onSavePickTicket(false)" :disabled="isSaving">
                    <i v-if="isSavingOnly" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>
                    Save
                </b-button>
                <b-button variant="primary" @click="onSavePickTicket(true)" :disabled="isSaving">
                    <i v-if="isSavingAndClose" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>
                    Save & Close
                </b-button>
            </template>
        </b-modal>
        <pick-list-confirmation
            :modalId="pickItemConfirmationModalId"
            :orderedItem="scheduleObj"
            :pickLineItem="pickLineData" />

        <fast-report-viewer ref="pick-ticket-reports-modal" :modalId="'pick-ticket-reports-modal'" :moduleName="'PickTicket'" :parameter="pickTicketId" />
    </section>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { date } from '@/shared/filters';
import { GRID_ID } from '@/shared/constants';
import { prepareColumnsData } from '@/shared/helpers';
import inventoryFields from './fields'
import PickListConfirmation from './components/PickListConfirmation';
import FastReportViewer from '@/shared/components/FastReportViewer';

export default {
    props: {
        sOScheduledObj: {
            type: Object,
            default: () => {}
        },
        pickTicketList: {
            type: Array,
            default: () => []
        },
        pickTicketListName: {
            type: String,
            default: 'pickTicketLinesList'
        },
        modalId: {
            type: String,
            default: 'pick-list-modal'
        }
    },
    components: {
        PickListConfirmation,
        FastReportViewer
    },
    data: () => ({
        date,
        fields: inventoryFields,
        prepareColumnsData,
        gridId: GRID_ID.PICK_LIST_GRID,
        inventoryItem: {},
        pickLineData: {},
        confirmingPickItem: {},
        isInvalidQtyToShip: false,
        isSaving: false,
        isSavingOnly: false,
        isSavingAndClose: false,
    }),
    computed: {
        ...mapGetters('account', ['gridSettingsById']),
        // ...mapGetters('inventory', ['allocationsList']),
        gridSettings() {
            return this.gridSettingsById(this.gridId);
        },
        scheduleObj() {
            return {
                ...this.sOScheduledObj,
                qtyToShip: (this.sOScheduledObj) ? this.sOScheduledObj.quantity : 0
            }
        },
        itemData() {
            return this.pickTicketList.map(line => ({
                ...line,
                qtyAllocated: line.quantityToPull,
                inventorySerialNumber: (line.inventorySerialNumber) ? line.inventorySerialNumber.map(sn => ({
                    ...sn,
                })) : []
            }));
        },
        showAddToInvoice() {
            return (this.itemData.length > 0) ? this.itemData.filter(x => x.isConfirmed == true).length > 0 : false;
        },
        pickItemConfirmationModalId() {
            return this.modalId + '-confirmation'
        },
        pickTicketId() {
            return this.itemData.length > 0 ? this.itemData[0].pickTicketId : 0 ;
        }
    },
    methods: {
        // ...mapActions('inventory', ['getAllocationsBySalesOrderLineId']),
        ...mapActions('pickTicket', [
            'getPickTicketLines',
            'savePickTicket'
        ]),

        onConfirmPickItem(pickItem) {
            console.log(pickItem, 'Pick Item');
            if (pickItem.selected && (pickItem.quantityToPull <= 0 || pickItem.quantityToPull > pickItem.quantityAllocated)) {
                let msg = pickItem.quantityToPull <= 0 ? 'The Qty To Pull must be greater than 0' : 'The Qty To Pull can not be more than ' + pickItem.quantityAllocated;
                this.openAlertBox({message: msg,
                    title: 'Please Check',okVariant: 'success',
                    headerClass: 'p-2 border-bottom-0',
                    footerClass: 'p-2 border-top-0',});
            } else if (!pickItem.selected) {
                this.openAlertBox({message: 'Please check the corresponding row before confirming',
                    title: 'Check before confirm',okVariant: 'success',
                    headerClass: 'p-2 border-bottom-0',
                    footerClass: 'p-2 border-top-0',});
            } else {
                this.pickLineData = pickItem;

                this.$bvModal.show(this.pickItemConfirmationModalId);
            }

            
        },
        isValidValues() {
            const pickedItems = this.itemData.filter(x => x.selected == true);
            let isInValid = false;

            if (pickedItems.length > 0) {
                this.isInvalidQtyToShip = pickedItems.map(item => parseInt(item.quantityToPull)).reduce((a, b) => a + b) > this.scheduleObj.qtyToShip;
                let qtyToPullErrorsCount = 0;

                for(let i=0; i < pickedItems.length; i++) {
                    const ndx = this.itemData.findIndex(x => x.id == pickedItems[i].id);
                    if (pickedItems[i].quantityToPull > pickedItems[i].qtyAllocated || pickedItems[i].quantityToPull == 0) {
                        this.itemData[ndx].isInvalidQtyToPull = true;
                        qtyToPullErrorsCount++;
                    } else {
                        this.itemData[ndx].isInvalidQtyToPull = false;
                    }
                }

                isInValid = this.isInvalidQtyToShip || qtyToPullErrorsCount > 0;

                if (isInValid) {
                  this.openAlertBox({message: 'Please correct errors',
                    title: 'Invalid',okVariant: 'success',
                    headerClass: 'p-2 border-bottom-0',
                    footerClass: 'p-2 border-top-0',});
                }
            }

            return !isInValid;
        },
        async onSavePickTicket(isClose) {
            if (!this.isValidValues()) {
                return;
            }
            try {
              this.isSaving = true;
              this.isSavingAndClose = isClose;
              this.isSavingOnly = !isClose;

              const existingPickTicket = this.itemData.filter(x => x.id > 0).length > 0 ? this.itemData.filter(x => x.id > 0)[0] : {
                pickTicketId: 0,
                createdByUserId: null
              };

              let pickTicketObj = {
                id: existingPickTicket.pickTicketId || 0,
                salesOrderLineId: this.scheduleObj.salesOrderLineId,
                salesOrderLineScheduleId: this.scheduleObj.id,
                quantity: parseInt(this.scheduleObj.qtyToShip),
                maxQuantity: this.scheduleObj.quantity,
                dateDue: this.scheduleObj.customerDueDate,
                shippingMethodId: this.scheduleObj.shippingMethodId,
                createdByUserId: existingPickTicket.createdByUserId || null,
                lines: this.itemData
              }

              await this.savePickTicket(pickTicketObj).then((res) => {
                this.$emit('refreshData');
                this.pickLineData = {};
                if (res.status == 200)
                  this.$snotify.success('Pick List Saved successfully');
              });

              this.isSaving = this.isSavingAndClose = this.isSavingOnly = false;
              if (isClose) {
                this.closeModal();
              }
            } catch (e) {
              this.isSaving = false;
              /*console.log(e.message);*/
            }
        },
        onAddToInvoice() {
            this.$emit('addToInvoice', this.sOScheduledObj);
            this.closeModal();
        },
        onPull(pullQty, id) {
            const ndx = this.pickTicketList.findIndex(x => x.id == id);
            if (ndx > -1) {
                this.pickTicketList[ndx].selected = (pullQty != '' && pullQty > 0);
            }
        },
        closeModal() {
            this.$bvModal.hide(this.modalId);
        },
        commafy(num, currency = "", decimal = 2) {
            try {
                if (num != null) {
                    if (currency != "") {
                        num = Number(num).toFixed(decimal);
                    }
                    var str = num.toString().split('.');
                    if (str[0].length >= 3) {
                        str[0] = str[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
                    }
                    if (str[1]) {
                        if (str[1].length < decimal) {
                        while (str[1].length < decimal) { str[1] = str[1] + "0"; }
                        } else if (str[1].length > decimal) {
                        str[1] = str[1].substring(0, decimal)
                        }
                    }
                    return currency + str.join('.');
                }
            } catch {
                return num;
            }
        },
        onPrint() {
            console.log(this.itemData, "Item Data")
        }
    },
}
</script>

<style scoped>
table > tr > th {
    margin-right: 10px;
}

.is-invalid-qty-to-ship {
    border-color: red;
}

.is-invalid-qty-to-pull {
    border-color: red;
}
</style>
